import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import DateRangeQuickPicker from "@/components/reports/DateRangeQuickPicker";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext } from "@/components/ui/pagination";
import { Search, ArrowUpDown, Layers, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { printDoc, pmoney } from "@/lib/print";
import { buildCustomReportHtml } from "@/components/reports/CustomReportBuilder";
import { downloadCustomReportCsv } from "@/lib/customReportExport";
import EmailReportDialog from "@/components/reports/EmailReportDialog";
import GetReportButton from "@/components/reports/GetReportButton";
import AccessRestricted from "@/components/AccessRestricted";

// R1-02: full, searchable, paginated product & variant sales report. Grouped server-side by the
// stable product_id (not display name) so renames/deletions never fragment or lose history.
// Does not touch or replace the existing Top Products chart on the Business tab.
const SORT_OPTIONS = [["revenue", "Sales Value"], ["qty", "Qty Sold"], ["name", "Name"], ["sku", "SKU"]];

export default function ProductSalesReport({ onAccessDenied }) {
  const { business, user } = useAuth();
  const cur = business?.currency || "JMD $";
  const canEmail = user?.role === "owner"; // R1-05: Owner-only for V1, no new Manager permission introduced.
  const [range, setRange] = useState({ start: new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) });
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [groupBy, setGroupBy] = useState("sku");
  const [sortBy, setSortBy] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [busy, setBusy] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const pageSize = 25;

  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [qDebounced, groupBy, range.start, range.end]);

  // R1-03 polish: the picker's Month/Year chevrons make rapid-click requests much more likely than
  // the old plain preset buttons — drop a response if a newer request has since started.
  const loadSeq = useRef(0);
  useEffect(() => {
    setLoading(true);
    const seq = ++loadSeq.current;
    api.get("/reports/products", { params: { ...range, q: qDebounced, group_by: groupBy, sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize } })
      .then((r) => { if (seq === loadSeq.current) { setData(r.data); setAccessDenied(false); } })
      .catch((e) => { if (seq === loadSeq.current && e.response?.status === 403) { setAccessDenied(true); onAccessDenied?.(); } })
      .finally(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [range.start, range.end, qDebounced, groupBy, sortBy, sortDir, page]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(key); setSortDir("desc"); }
  };

  // R1-04B: reuses the exact same /reports/custom?sections=products pipeline (full, non-paginated,
  // bounded/truncation-flagged dataset — the same one the Custom Report Builder's "All Products
  // Sold" section already uses) and the same buildCustomReportHtml renderer, so this prints every
  // qualifying row for the current period/grouping/search filter, not just the visible page.
  // Window is opened SYNCHRONOUSLY on click (see lib/print.js's `win` note) and carries the
  // explicit "Print Now" fallback button (`printButton: true`) that the Owner confirmed works.
  const doPrint = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Please allow pop-ups to print this report"); return; }
    w.document.write('<!doctype html><html><body style="font-family:sans-serif;padding:60px;color:#64748b">Preparing your report…</body></html>');
    (async () => {
      setPrinting(true);
      try {
        const { data: d } = await api.get("/reports/custom", { params: { start: range.start, end: range.end, sections: "products", group_by: groupBy, q } });
        printDoc({
          business, docTitle: "Product Sales Report", docRef: "",
          meta: [["Period", `${range.start} → ${range.end}`], ["Grouping", groupBy === "parent" ? "Parent Rollup" : "SKU / Variant"], ...(q ? [["Search filter", q]] : [])],
          body: buildCustomReportHtml(d, cur), accent: "#f59e0b", plain: true, pageNumbers: true,
          footerNote: `Product Sales Report  ·  Period: ${range.start} → ${range.end}  ·  Generated ${new Date().toLocaleString()}`,
          win: w, printButton: true,
        });
      } catch {
        w.close();
        toast.error("Could not build the report — please try again");
      }
      setPrinting(false);
    })();
  };

  // Reports Final UI Polish: this tab never had a standalone Download CSV button — wires the
  // "Get Report" menu's CSV item to the existing /reports/custom/csv endpoint (sections=products),
  // carrying the same grouping/search filter already used by Print and Email. No backend change.
  // NOTE for Owner review: this is a newly introduced CSV capability for this tab (Products never
  // had a CSV export before this build).
  const psrParams = () => ({ start: range.start, end: range.end, sections: "products", group_by: groupBy, q: qDebounced });
  const doCsv = async () => {
    setBusy("csv");
    const err = await downloadCustomReportCsv(psrParams(), range);
    if (err) toast.error(err);
    setBusy("");
  };

  const totalPages = data?.total_pages || 1;
  const pageNumbers = useMemo(() => {
    const n = totalPages;
    if (n <= 7) return Array.from({ length: n }, (_, i) => i + 1);
    const set = new Set([1, 2, n - 1, n, page - 1, page, page + 1]);
    return Array.from(set).filter((x) => x >= 1 && x <= n).sort((a, b) => a - b);
  }, [totalPages, page]);

  if (accessDenied) return <AccessRestricted module="Reports" reason="manager_reports_off" />;

  return (
    <div className="space-y-4" data-testid="product-sales-report">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeQuickPicker value={range} onChange={(start, end) => setRange({ start, end })} testidPrefix="psr-quick" startTestId="psr-start" endTestId="psr-end" />
          <div className="relative flex-1 min-w-[200px]">
            <Label className="text-xs">Search</Label>
            <Search className="w-4 h-4 absolute left-3 top-1/2 translate-y-1 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Product name, SKU or variant…" className="pl-9" data-testid="psr-search" />
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant={groupBy === "sku" ? "default" : "outline"} onClick={() => setGroupBy("sku")} data-testid="psr-group-sku"><Tag className="w-3.5 h-3.5 mr-1" /> By SKU/Variant</Button>
            <Button size="sm" variant={groupBy === "parent" ? "default" : "outline"} onClick={() => setGroupBy("parent")} data-testid="psr-group-parent"><Layers className="w-3.5 h-3.5 mr-1" /> By Parent (rolled up)</Button>
          </div>
          <GetReportButton onPrint={doPrint} onDownloadCsv={doCsv} onEmail={() => setShowEmail(true)} canEmail={canEmail} busy={printing ? "print" : busy} testidPrefix="psr" />
        </div>
      </Card>
      <EmailReportDialog open={showEmail} onOpenChange={setShowEmail} sections={["products"]} range={range} groupBy={groupBy} q={qDebounced} reportLabel="All Products Sold" />

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="psr-grand-totals">
          <Card className="p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Qty Sold</p><p className="mono font-bold text-lg">{data.grand_totals.qty_sold}</p></Card>
          <Card className="p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Qty Returned</p><p className="mono font-bold text-lg text-rose-500">{data.grand_totals.qty_returned}</p></Card>
          <Card className="p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Net Qty</p><p className="mono font-bold text-lg">{data.grand_totals.net_qty}</p></Card>
          <Card className="p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Net Sales Value</p><p className="mono font-bold text-lg text-emerald-500">{money(data.grand_totals.net_revenue)}</p></Card>
        </div>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="psr-table">
          <thead>
            <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
              <Th label="Product" k="name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <Th label="SKU" k="sku" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <th className="text-left py-2 px-3">Variant</th>
              <Th label="Qty Sold" k="qty" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <th className="text-right py-2 px-3">Returns</th>
              <th className="text-right py-2 px-3">Net Qty</th>
              <Th label="Sales Value" k="revenue" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="psr-loading">Loading…</td></tr>
            ) : !data || data.rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="psr-empty">No product sales in this period.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.product_id || r.name} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`psr-row-${r.product_id || r.name}`}>
                <td className="py-2 px-3">
                  {r.name}
                  {!r.in_catalog && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-500" data-testid={`psr-deleted-${r.product_id}`}>(no longer in catalog)</span>}
                  {r.is_parent && groupBy === "sku" && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-sky-500">Parent</span>}
                </td>
                <td className="py-2 px-3 mono text-xs text-muted-foreground">{r.sku || "—"}</td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{r.variant || "—"}</td>
                <td className="py-2 px-3 mono text-right">{r.qty_sold}</td>
                <td className="py-2 px-3 mono text-right text-rose-500">{r.qty_returned ? `−${r.qty_returned}` : "—"}</td>
                <td className="py-2 px-3 mono text-right font-semibold">{r.net_qty}</td>
                <td className="py-2 px-3 mono text-right font-semibold">{money(r.net_revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {data && data.total_rows > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground" data-testid="psr-pagination-summary">
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, data.total_rows)} of {data.total_rows} products sold
          </p>
          {totalPages > 1 && (
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem><PaginationPrevious href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); }} className={page === 1 ? "pointer-events-none opacity-40" : ""} data-testid="psr-page-prev" /></PaginationItem>
                {pageNumbers.map((n) => <PaginationItem key={n}><PaginationLink href="#" isActive={n === page} onClick={(e) => { e.preventDefault(); setPage(n); }} data-testid={`psr-page-${n}`}>{n}</PaginationLink></PaginationItem>)}
                <PaginationItem><PaginationNext href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.min(totalPages, p + 1)); }} className={page === totalPages ? "pointer-events-none opacity-40" : ""} data-testid="psr-page-next" /></PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
    </div>
  );
}

const Th = ({ label, k, sortBy, sortDir, onClick, align }) => (
  <th className={`py-2 px-3 cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"}`} onClick={() => onClick(k)} data-testid={`psr-sort-${k}`}>
    <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>{label} <ArrowUpDown className={`w-3 h-3 ${sortBy === k ? "text-foreground" : "opacity-30"}`} />{sortBy === k && <span className="text-[10px]">{sortDir === "desc" ? "▼" : "▲"}</span>}</span>
  </th>
);
